import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormLabel, HStack, SimpleGrid, Stack } from '@chakra-ui/react';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import TaskTypeSelect from 'features/Work/components/TaskTypeSelect';
import { InvoiceFormSchema } from 'features/Work/form/invoiceForm.schema';
import { QuoteFormSchema } from 'features/Work/form/quoteForm.schema';

import VatInput from '../../../../VatInput';
import locales from './locales';

interface TaskFormProps {
  allowDelete: boolean;
  index: number;
  isFromCatalog: boolean;
  onDelete: () => void;
}

const TaskForm: FC<TaskFormProps> = ({
  allowDelete,
  index,
  isFromCatalog,
  onDelete,
}) => {
  const { control } = useFormContext<InvoiceFormSchema | QuoteFormSchema>();

  return (
    <SunshineCard>
      <SunshineCard.Content>
        <Stack spacing="space-16">
          <Controller
            control={control}
            name={`tasks.${index}.title`}
            render={({ field, fieldState }) => (
              <Form.Field error={fieldState.error?.message}>
                <Stack spacing="space-4">
                  <HStack spacing="space-8">
                    {isFromCatalog ? null : (
                      <FormLabel>{locales.fields.title}</FormLabel>
                    )}
                  </HStack>

                  {isFromCatalog ? (
                    <Text fontWeight="weight-500" variant="primary">
                      {field.value}
                    </Text>
                  ) : (
                    <Form.Input
                      {...field}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                </Stack>
              </Form.Field>
            )}
          />

          <Controller
            control={control}
            name={`tasks.${index}.subtitle`}
            render={({ field }) => (
              <Form.Field label={locales.fields.description}>
                <Form.Textarea
                  {...field}
                  onChange={(e) => field.onChange(e.target.value)}
                  value={field.value ?? ''}
                />
              </Form.Field>
            )}
          />

          <Controller
            control={control}
            name={`tasks.${index}.type`}
            render={({ field, fieldState }) => (
              <Form.Field
                error={fieldState.error?.message}
                label={locales.fields.type}
              >
                {isFromCatalog ? (
                  <Text fontWeight="weight-500" variant="primary">
                    {field.value ? locales.type[field.value] : ''}
                  </Text>
                ) : (
                  <TaskTypeSelect
                    onChange={(option) => field.onChange(option?.value)}
                    value={field.value}
                  />
                )}
              </Form.Field>
            )}
          />

          <SimpleGrid columns={3} spacing="space-8">
            {/* unit price */}
            <Controller
              control={control}
              name={`tasks.${index}.unitPrice`}
              render={({ field, fieldState }) => (
                <Form.Field
                  error={fieldState.error?.message}
                  label={locales.fields.unitPrice}
                >
                  <Form.Input
                    {...field}
                    onChange={({ target: { value } }) =>
                      field.onChange(value === '' ? value : Number(value))
                    }
                    rightIcon="euro"
                    type="number"
                  />
                </Form.Field>
              )}
            />

            {/* quantity */}
            <Controller
              control={control}
              name={`tasks.${index}.units`}
              render={({ field, fieldState }) => (
                <Form.Field
                  error={fieldState.error?.message}
                  label={locales.fields.quantity}
                >
                  <Form.Input
                    {...field}
                    onChange={({ target: { value } }) =>
                      field.onChange(value === '' ? value : Number(value))
                    }
                    type="number"
                  />
                </Form.Field>
              )}
            />

            <Controller
              control={control}
              name={`tasks.${index}.vatPercentage`}
              render={({ field, fieldState }) => (
                <Form.Field
                  error={fieldState.error?.message}
                  label={locales.fields.vat}
                >
                  <VatInput
                    onChange={(value) =>
                      field.onChange(value === '' ? value : Number(value))
                    }
                    value={field.value}
                  />
                </Form.Field>
              )}
            />
          </SimpleGrid>
        </Stack>

        {allowDelete ? (
          <Button
            alignSelf="flex-start"
            icon="trash"
            iconPosition="left"
            marginTop="space-24"
            onClick={onDelete}
            variant="inline-secondary"
          >
            {locales.delete}
          </Button>
        ) : null}
      </SunshineCard.Content>
    </SunshineCard>
  );
};

export default TaskForm;
